import React, { useState, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import employerSvc from "../../services/employer/employerSvc";
import { PLAN_TYPE_MAP } from "../../utils/constants";
import EnrollData from "./components/EnrollData";

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    paddingLeft: theme.spacing(1),
  },
}));

function SearchEnroll() {
  const location = useLocation();
  const employerId = location.pathname.split("/")[2];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [employerInfo, setEmployerInfo] = useState({ employerName: null, employerState: null });

  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showAvailablePlans, setShowAvailablePlans] = useState(!!employerId);
  const [noPlansFound, setNoPlansFound] = useState(false);
  const [plansFetched, setPlansFetched] = useState(false);
  
  const isBelow900 = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    const getEmployers = async () => {
      try {
        setLoading(true);
        const response = await employerSvc.getEmployersByWebsite({
          website: "WEA Benefits",
          page: "enrollSearch",
        });
        setEmployers(response);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getEmployers();
  }, []);

  useEffect(() => {
    if (employerId) {
      const getEmployerDetails = async () => {
        try {
          setLoading(true);
          const { employerInfo, plans, approvedVendors } = await employerSvc.getEmployerDetails({
            employerIdForURL: employerId,
            page: "enrollSearch",
          });
          setPlans(plans);
          setApprovedVendors(approvedVendors);
          setEmployerInfo(employerInfo);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
          setPlansFetched(true);
        }
      };
      getEmployerDetails();
    }
  }, [employerId]);

  useEffect(() => {
    const planTypeObjects = plans.map((item) => {
      const key = item.planUniqueId.split("_")[1];
      return { label: PLAN_TYPE_MAP[key]?.label || key };
    });
    setPlanTypes(planTypeObjects);

    if (employerId && employers.length > 0) {
      const matched = employers.find((e) => e.employerIdForURL === employerId);
      setSelectedEmployer(matched);
      setShowAvailablePlans(!!matched);

      if (plansFetched && plans.length === 0) {
        setNoPlansFound(true);
      } else {
        setNoPlansFound(false);
      }
    } else {
      setNoPlansFound(false);
    }
  }, [plans, employerId, employers, plansFetched]);

  return (
    <Container
      component="main"
      maxWidth="sm"
      /*
        Use breakpoints to keep it responsive:
        - No top/bottom margin on larger screens
        - Slight margin on mobile
      */
      sx={{
        mt: { xs: 2, md: 0 },
        mb: { xs: 2, md: 0 },
      }}
    >
      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress size={40} />
        </Grid>
      ) : (
        <Card sx={{ p: { xs: 2, md: 3 }, boxShadow: "none" }}>
              <CardContent>
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "center",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      // Responsive font size: 18px for desktop
                      fontSize: { xs: "14px", sm: "16px", md: "18px" },
                      mb: { xs: 2, sm: 3, md: 4 },
                    }}
                  >
                    {isBelow900
                      ? "Employer Search:"
                      : "Begin by entering the name of your employer:"}
                  </Typography>
        
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={9}>
                      <StyledAutocomplete
                        id="clientSearchBar"
                        value={selectedEmployer}
                        inputValue={searchValue}
                        open={searchValue.length >= 3 && selectedEmployer === null}
                        forcePopupIcon={false}
                        getOptionLabel={(option) => option.employerName || ""}
                        onChange={(_, employer) => setSelectedEmployer(employer)}
                        onInputChange={(_, value) => setSearchValue(value)}
                        options={employers}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Employer Name"
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        component={RouterLink}
                        to={selectedEmployer ? `/search-enroll/${selectedEmployer.employerIdForURL}` : ""}
                        disabled={!selectedEmployer}
                        sx={{
                          fontWeight: 500,
                          backgroundColor: "#041E42",
                          "&:hover": { backgroundColor: "#041E42" },
                          fontSize: isMobile ? "0.8rem" : "1rem",
                          padding: isMobile ? "6px 10px" : "8px 16px",
                        }}
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
        
                  {searchValue.length >= 3 && employers.length === 0 && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: "center" }}>
                      No employers found.
                    </Typography>
                  )}
                </Box>
        
                {showAvailablePlans && planTypes.length > 0 ? (
                  <EnrollData
                    planTypes={planTypes}
                    plans={plans}
                    employerName={employerInfo.employerName}
                    approvedVendors={approvedVendors}
                    empState={employerInfo.state}
                    reliusWeb={selectedEmployer?.relius_web}
                    employerId={employerId}
                  />
                ) : showAvailablePlans && noPlansFound ? (
                  <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="error">
                      No Plans Found. If you believe this is a mistake, please contact 1-800-279-4030.
                    </Typography>
                  </Box>
                ) : null}
              </CardContent>
            </Card>
      )}
    </Container>
  );
}

export default SearchEnroll;
